// Idempotent /users/{uid} upsert. Runs on every sign-in so a Firestore
// profile exists for every Auth account, regardless of provider or whether
// the onUserCreated trigger fired. Uses setDoc(merge:true) — never clobbers
// role, linkedMemberId, or other admin-managed fields.

import type { User } from 'firebase/auth'
import { typedDoc, setDocument, getDocument, serverTimestamp } from '@/lib/firebase/firestore'
import type { UserDocument } from '@/types/firebase.types'

export async function ensureUserProfile(user: User): Promise<void> {
  const ref = typedDoc<UserDocument>('users', user.uid)
  const existing = await getDocument(ref)

  const base = {
    uid: user.uid,
    email: user.email ?? '',
    displayName: user.displayName ?? null,
    photoURL: user.photoURL ?? null,
    updatedAt: serverTimestamp(),
  }

  if (!existing) {
    await setDocument(
      ref,
      {
        ...base,
        role: 'user',
        createdAt: serverTimestamp(),
      } as unknown as UserDocument
    )
    return
  }

  // Refresh mutable Auth-derived fields only; preserve role/linkedMemberId.
  const needsRefresh =
    existing.email !== base.email ||
    existing.displayName !== base.displayName ||
    existing.photoURL !== base.photoURL
  if (!needsRefresh) return

  await setDocument(ref, { ...existing, ...base } as unknown as UserDocument)
}
